import cn from 'clsx'
import { RouterLocation, replace } from 'connected-react-router'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { rem } from 'polished'
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { connect } from 'react-redux'
import styled from 'styled-components'

import Pane from 'components/Common/Pane'
import OfferPageGallery from '../OfferPageGallery'
import PackageOptions from './FlashHotelPackageOptions'

import { setRecentlyUsedAirportCode } from 'actions/AuthActions'
import { clearBookingCalendar, fetchCalendars, fetchCalendarsByOccupancy } from 'actions/CalendarActions'
import { fetchOfferFlightPrice, fetchRelatedTravelItems } from 'actions/OfferActions'
import { CLEAR_OFFER_CHECK_IN_CHECK_OUT, SET_OFFER_CHECK_IN_CHECK_OUT } from 'actions/actionConstants'
import * as Analytics from 'analytics/analytics'
import { offerPageHotLeadEvent } from 'analytics/snowplow/events'
import Breakpoint from 'components/Common/Breakpoint'
import LayoutContainer from 'components/Common/LayoutContainer'
import RedirectWithStatus from 'components/Common/RedirectWithStatus'
import ScrollAnchor from 'components/Common/ScrollAnchor'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import VideoLightbox from 'components/Luxkit/Carousel/VideoLightbox'
import LightBoxContext from 'components/Luxkit/Modal/LightBoxContext'
import Subtitle from 'components/Luxkit/Typography/Subtitle'
import DesktopRelatedTravelItems from 'components/OfferPage/RelatedTravelItems'
import ExpiredDealsLereRecommendations from 'components/Recommendations/ExpiredDeals/ExpiredDealsLereRecommendations'
import useIdleTimeReporter, { IdleTimeSource } from 'components/Recommendations/hooks/useIdleTimeReporter'
import Backdrop from 'components/utils/Backdrop'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import config from 'constants/config'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { urlHashDatePicker } from 'constants/url'
import OfferPageStateProvider from 'contexts/OfferPage/offerPageStateProvider'
import { OfferPageActions, OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import GeoContext from 'contexts/geoContext'
import useOfferPageReducer from 'hooks/OfferPage/useOfferPageReducer'
import { useSaveHotLead } from 'hooks/Recommendation/useSaveHotLead'
import useMountedEffect from 'hooks/useMountedEffect'
import usePrevious from 'hooks/usePrevious'
import { EmptyArray, nonNullable } from 'lib/array/arrayUtils'
import { generateRoomOccupants, sumUpOccupancies } from 'lib/offer/occupancyUtils'
import offerPageURL from 'lib/offer/offerPageURL'
import { hasOfferExpired } from 'lib/offer/offerUtils'
import { removeSearchParamsFromURL } from 'lib/search/searchUtils'
import { multiRoomEnabled } from 'selectors/featuresSelectors'
import { getInitialLeOfferPageState } from 'selectors/offerPage/offerPageSelectors'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton/BookmarkButton'
import ImmersiveSaveButtonPortal from 'tripPlanner/components/ImmersiveHeader/ImmersiveSaveButtonPortal'
import { useSyncHotelTripItem } from 'tripPlanner/hooks/bookmarks/useSyncHotelTripItem'
import { SetRequired } from 'type-fest'
import { isFlightForceBundled } from 'lib/offer/isFlightForceBundled'
import OfferAirportSelect from '../Filters/Airport/OfferAirportSelect'
import OfferAirportSelectBox from '../Filters/Airport/OfferAirportSelectBox'
import OfferCalendarDatePickerSelect from '../Filters/CalendarPicker/OfferCalendarDatePickerSelect'
import OfferCalendarDatePickerSelectBox from '../Filters/CalendarPicker/OfferCalendarDatePickerSelectBox'
import OfferCapacityWithAgesSelect from '../Filters/CapacityWithAges/OfferCapacityWithAgesSelect'
import OfferCapacityWithAgesSelectBox from '../Filters/CapacityWithAges/OfferCapacityWithAgesSelectBox'
import OfferDurationTabs from '../Filters/DurationTabs/OfferDurationTabs'
import MultipleRoomsBookingContent from '../MultipleRoomsBooking/MultipleRoomsBookingContent'
import OfferUpgradeModalContext from '../OfferUpgradeModalContext'
import useIsAvailableForPurchase from '../hooks/useIsAvailableForPurchase'
import OfferIntro from './FlashHotelIntro'
import FlashHotelSidebarFilters from './FlashHotelSidebarFilters'
import FlashHotelHeader from './FlashHotelStickyHeader'
import { getShareModalConfig } from 'lib/customer/getShareModalConfig'
import HotelOfferPageStickyFooter from '../HotelOfferPageStickyFooter'
import ProductPaletteProvider, { ProductPalette } from 'contexts/ProductPaletteContext'
import LuxuryPlusNotificationBanner from 'loyalty/components/LuxuryPlusNotificationBanner'
import HotelFlightsToggleButton from '../HotelFlightsToggleButton'
import { OptimizelyClientSideExperiments, OptimizelyFeatureFlags, getOptimizelyFeatureFlagVariation, useOptimizelyExperiment } from 'lib/optimizely/optimizelyUtils'
import OfferDurationSelect from '../Filters/Duration/OfferDurationSelect'
import OfferDurationSelectBox from '../Filters/Duration/OfferDurationSelectBox'
import { interactDuration } from 'analytics/optimizely/events'
import { track } from 'analytics/analytics'

const RelativeBlock = styled.form`
  position: relative;
`

const StyledFlashHotelStickyHeader = styled(FlashHotelHeader)`
  position: relative;

  &.hideOnMobile.hideOnMobile {
    display: none;

    ${mediaQueryUp.tablet} {
      display: flex;
      visibility: visible;
    }
  }
`

const PackageOptionsPane = styled(Pane)`
  padding: ${rem(20)} 0;
`

const DesktopFilterBlock = styled(Group)`
  &.open {
    position: relative;
    z-index: 3;
  }
`

const StyledOfferDurationSelect = styled(OfferDurationSelect)`
  flex-grow: 1;
  max-width: ${rem(250)};
`

const StyledOfferCapacityWithAgesSelect = styled(OfferCapacityWithAgesSelect)`
  flex-grow: 1;
  max-width: ${rem(250)};
`

const StyledOfferCalendarDatePickerSelect = styled(OfferCalendarDatePickerSelect)`
  flex-grow: 1;
  max-width: ${rem(250)};
`

const StyledOfferAirportSelect = styled(OfferAirportSelect)`
  flex-grow: 1;
  max-width: ${rem(250)};
`

type CartDefaults = Partial<OfferPageState>

interface Props {
  defaults: CartDefaults;
  offer: App.HotelOffer;
  originAirports?: Array<App.Airport>;
  airports?: Array<App.Airport>;
  routerLocation: RouterLocation<unknown>;
  currency: string;
  isMultiRoomEnabled: boolean;
  lereOfferExpired: boolean;
  regionCode: string;
  headlessMode: boolean;
  customerPortalURL: string
  memberId: string
  pathname: string
}

const fallbackRooms = [generateRoomOccupants()]

function FlashHotelContent(props: Props) {
  const {
    offer,
    originAirports = EmptyArray,
    airports = EmptyArray,
    defaults,
    routerLocation,
    isMultiRoomEnabled,
    regionCode,
    headlessMode,
    lereOfferExpired,
    customerPortalURL,
    memberId,
    pathname,
  } = props

  const {
    id,
    packages,
  } = offer

  // calendars only start fetching once the user has tried to look at dates
  // after that, try keep the calendars up to date
  const [viewedDates, setViewedDates] = useState(!!defaults.checkInDate)
  const [state, stateDispatch, stateSnapshotRef] = useOfferPageReducer({
    ...defaults,
    duration: defaults.duration ?? offer.lowestPricePackage?.duration,
    offer,
    flights: offer.bundledWithFlightsOnly,
  })

  const {
    airport,
    duration,
    activeFilter,
    rooms = fallbackRooms,
    package: selectedPackage,
    checkInDate,
    flights: isFlightsSelected,
  } = state

  const geoState = useContext(GeoContext)
  const dispatch = useAppDispatch()
  const packageOptionsAnchor = useRef<HTMLDivElement>(null)
  const scrollAfterFilterClose = useRef<boolean>(false)
  const previousState = usePrevious<OfferPageState>(state, {})
  const previousFilter = usePrevious(activeFilter)
  const lastFetchedRooms = useRef<Array<App.RoomOccupants>>([])
  const lastFetchedAirport = useRef<App.Airport>()
  const offerDurationSelectRef = useRef<HTMLDivElement>(null)
  const canBookMultiRoomTypes = isMultiRoomEnabled && rooms.length > 1
  const isHotelOfferStickyFooterEnabled = !!useAppSelector(state => getOptimizelyFeatureFlagVariation(state, OptimizelyFeatureFlags.isHotelOfferStickyFooterEnabled))

  useSyncHotelTripItem(state, offer)

  useIdleTimeReporter(IdleTimeSource.FlashHotel)

  const isHotelAndFlightsToggleEnabled = !!useOptimizelyExperiment(OptimizelyClientSideExperiments.offerPageHotelAndFlightsToggle)
  const isNumberOfNightsToggleEnabled = !!useOptimizelyExperiment(OptimizelyClientSideExperiments.offerPageNumberOfNights)

  const isoCheckIn = state.checkInDate?.format(ISO_DATE_FORMAT)
  const isoCheckOut = state.checkInDate?.clone().add(duration, 'days').format(ISO_DATE_FORMAT)

  // Need to pass the context state above the tree
  useEffect(() => {
    if (state.offer) {
      dispatch({
        type: SET_OFFER_CHECK_IN_CHECK_OUT,
        payload: {
          offerId: state.offer.id,
          checkIn: isoCheckIn,
          checkOut: isoCheckOut,
          duration,
          rooms,
        },
      })
    }
    return () => {
      dispatch({
        type: CLEAR_OFFER_CHECK_IN_CHECK_OUT,
        payload: {
          offerId: state.offer?.id,
        },
      })
    }
  }, [dispatch, state.offer, isoCheckIn, isoCheckOut, duration, rooms])

  useEffect(() => {
    dispatch(fetchRelatedTravelItems(offer, isoCheckIn, isoCheckOut, rooms))
  }, [dispatch, offer, isoCheckIn, isoCheckOut, rooms])

  useMountedEffect(() => {
    scrollAfterFilterClose.current = scrollAfterFilterClose.current ||
    previousState.rooms !== rooms ||
    previousState.checkInDate !== checkInDate
  }, [state.rooms, state.checkInDate])

  const trackInteractionDurationEvent = useCallback(() => {
    track('interact-duration', interactDuration())
  }, [])

  const scrollToPackageOptions = useCallback(() => {
    requestAnimationFrame(() => {
      packageOptionsAnchor.current?.scrollIntoView({ behavior: 'auto' })
    })
  }, [])

  const onViewPackageOptions = useCallback(() => {
    scrollToPackageOptions()
  }, [scrollToPackageOptions])

  const fetchCalendarData = useCallback(() => {
    if (
      !offer.packages.length ||
      rooms.some(room => room.childrenAge?.some(age => age === -1))
    ) {
      // if we have any invalid ages or no packages, don't try fetch - it'll fail anyway
      return
    }

    if (canBookMultiRoomTypes) {
      dispatch(fetchCalendarsByOccupancy(id, {
        occupants: rooms,
        flightOrigin: airport?.code,
        packages: offer.packages.filter(p => p.duration === duration),
      }))
    } else {
      dispatch(fetchCalendars(id, {
        occupants: rooms,
        flightOrigin: airport?.code,
        packages: nonNullable([selectedPackage ?? offer.lowestPricePackage]),
      }))
      dispatch(fetchCalendars(id, {
        occupants: rooms,
        flightOrigin: airport?.code,
        packages: offer.packages.filter(p => p.duration === duration),
      }))
    }

    lastFetchedRooms.current = rooms
    lastFetchedAirport.current = airport
  }, [airport, canBookMultiRoomTypes, dispatch, duration, id, offer, rooms, selectedPackage])

  const showLightbox = useContext(LightBoxContext)

  const watchVideo = useCallback(() => {
    if (offer.vimeoVideoId) {
      showLightbox(<VideoLightbox videoId={offer.vimeoVideoId} playOnMount />)
    }
  }, [offer, showLightbox])

  useEffect(() => {
    const hasRooms = rooms !== fallbackRooms
    const hasDate = !!checkInDate
    const hasInvalidChildren = rooms.some(room => room.childrenAge?.some(age => age === -1))

    if (previousFilter === 'capacity') {
      if (!hasRooms) {
        // user looked at capacity, didn't change anything and closed it
        // we consider this them applying the default rooms, so dispatch a proper update
        stateDispatch({
          type: OfferPageActions.setRooms,
          rooms: [generateRoomOccupants()],
        })

        if (!activeFilter) {
          scrollToPackageOptions()
          scrollAfterFilterClose.current = false
        }
      }
      if (!activeFilter && !hasDate && !hasInvalidChildren) {
        const nextFilter = isNumberOfNightsToggleEnabled ? 'duration' : 'checkin'
        stateDispatch({ type: OfferPageActions.toggleFilter, filter: nextFilter })
      }
    }

    if (activeFilter === 'checkin' && hasInvalidChildren) {
      // trying to open the calendar with invalid children ages, means we can't fetch calendar
      // direct them to the capacity instead to fill it in
      stateDispatch({ type: OfferPageActions.toggleFilter, filter: 'capacity' })
    }

    if (previousFilter === 'checkin' && !activeFilter && hasDate && !hasRooms) {
      stateDispatch({ type: OfferPageActions.toggleFilter, filter: 'capacity' })
    }

    if (activeFilter) {
      scrollToPackageOptions()
    }

    if (previousFilter && !activeFilter && hasRooms && hasDate) {
      if (scrollAfterFilterClose.current) {
        if (state.room) {
          window.location.hash = state.room.id
        } else {
          scrollToPackageOptions()
        }
      }
      scrollAfterFilterClose.current = false
    }

    if (activeFilter === 'checkin' && !viewedDates) {
      setViewedDates(true)
      fetchCalendarData()
    } else {
      if (viewedDates && (lastFetchedAirport.current !== airport || (!canBookMultiRoomTypes && lastFetchedRooms.current !== rooms))) {
        // rooms/airport has changed, all loaded calendars are no longer valid as we load calendars for a specific
        // combinations of flight origin (flight prices) and rooms (availability), so clear them
        dispatch(clearBookingCalendar())
        fetchCalendarData()
      } else if (canBookMultiRoomTypes && lastFetchedRooms.current !== rooms) {
        // if multiple room booking is available and we changed the rooms,
        // don't clear the previous data, just update for the new changed
        // rooms (is filtered inside fetchCalendarsByOccupancy action)
        fetchCalendarData()
      }
    }
  }, [activeFilter, fetchCalendarData]) // eslint-disable-line react-hooks/exhaustive-deps

  useMountedEffect(() => {
    stateDispatch({ type: OfferPageActions.setOffer, offer })
  }, [offer])

  useEffect(() => {
    // once we load packages and have dates, get our calendar data
    if (viewedDates && offer.packages) {
      fetchCalendarData()
    }
  // eslint-disable-next-line
  }, [offer.packages])

  const hotLead = useSaveHotLead({
    offerId: id,
    offerType: offer.type,
    checkInDate,
    duration,
    occupancy: rooms,
    defaultOccupancy: fallbackRooms,
  })

  useEffect(() => {
    if (hotLead?.checkIn && hotLead.checkOut) {
      Analytics.track('offer-page-hot-lead-event', offerPageHotLeadEvent(offer, hotLead, geoState))
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotLead])

  useMountedEffect(() => {
    // effectively listening on 'on duration change' event
    if (viewedDates) {
      fetchCalendarData()
    }

    if (!activeFilter) {
      scrollToPackageOptions()
    }
  }, [duration])

  useMountedEffect(() => {
    if (airport) {
      // effectively an 'on airport change' event
      dispatch(setRecentlyUsedAirportCode(airport.code))
      dispatch(fetchOfferFlightPrice(id, airport.code))
    }
  }, [airport])

  useEffect(() => {
    if (routerLocation.search) {
      const newSearch = removeSearchParamsFromURL(routerLocation.search)
      dispatch(replace({ ...routerLocation, search: newSearch }))
    }

    if (routerLocation.hash === urlHashDatePicker) {
      stateDispatch({ type: OfferPageActions.toggleFilter, filter: 'checkin' })
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const occupancyNumber = useMemo(() => sumUpOccupancies(rooms), [rooms])

  const occupancyLimits = useMemo(() => {
    return packages.flatMap((p) => p.roomRate.capacities)
  }, [packages])

  const isValidRoomOccupancy = offer.bundledWithFlightsOnly || occupancyNumber < 10
  const destinationAirport = useMemo(() => airports.find(({ code }) => code === offer.flightDestinationPort), [airports, offer.flightDestinationPort])
  const showFlightInformation = offer.offerFlightsEnabled && destinationAirport && (originAirports.length > 1 || (originAirports.length > 0 && destinationAirport.code !== airport?.code)) && isValidRoomOccupancy && config.businessTraveller.currentAccountMode !== 'business' && (!isHotelAndFlightsToggleEnabled || isFlightsSelected || offer.bundledWithFlightsOnly)
  const availableForPurchase = useIsAvailableForPurchase(state)

  const hideSoldOutOnMobile = !availableForPurchase && config.LERE_EXPIRED_DEALS_V2_ENABLED

  if (offer.altLink) {
    return <RedirectWithStatus code={301} to={`/${regionCode.toLowerCase()}${offerPageURL(offer)}`} />
  }
  const location = [offer.locationHeading, offer.locationSubheading].filter(t => t).join(', ')
  const shareModalConfig = getShareModalConfig({
    customerPortalURL,
    image: offer.images[0],
    memberId,
    offerId: offer.id,
    offerLocation: location ?? '',
    pathname,
    region: regionCode,
    type: 'offer',
    utmCampaign: 'webshare',
  })
  const paletteValue: ProductPalette = offer.loyalty?.earlyAccess ? 'loyalty' : 'default'
  return (
    <ProductPaletteProvider palette={paletteValue}>
      <OfferPageStateProvider state={state} stateSnapshotRef={stateSnapshotRef} dispatch={stateDispatch}>
        <ImmersiveSaveButtonPortal>
          <BookmarkButton offer={offer} />
        </ImmersiveSaveButtonPortal>
        <OfferUpgradeModalContext>
          <RelativeBlock onSubmit={(e) => e.preventDefault()}>
            <CSSBreakpoint min={!isHotelOfferStickyFooterEnabled ? 'mobile' : 'tablet'}>
              <StyledFlashHotelStickyHeader
                  offer={offer}
                  onViewPackageOptions={onViewPackageOptions}
                  className={cn({ hideOnMobile: hideSoldOutOnMobile })}
                >
                {!headlessMode && <DesktopRelatedTravelItems offer={offer} />}
              </StyledFlashHotelStickyHeader>
            </CSSBreakpoint>
            {offer.loyalty?.earlyAccess &&
              <LuxuryPlusNotificationBanner text="Members get early access to this offer before the general public and more"/>
              }
            {availableForPurchase &&
              <Breakpoint max="mobile">
                <FlashHotelSidebarFilters
                    onViewPackageOptions={scrollToPackageOptions}
                    capacities={occupancyLimits}
                  />
              </Breakpoint>
            }
            {lereOfferExpired && (
              <div>
                <ExpiredDealsLereRecommendations
                  hideOffers={!!activeFilter}
                />
              </div>
            )}
            <OfferPageGallery offer={offer}/>
            <Pane>
              <OfferIntro
                onWatchVideo={watchVideo}
                onViewPackageOptions={onViewPackageOptions}
                flightDestination={destinationAirport}
                shareModalConfig={shareModalConfig}
              />
            </Pane>
            <ScrollAnchor ref={packageOptionsAnchor} />
            <PackageOptionsPane id="Package-Options">
              <LayoutContainer>
                <VerticalSpacer gap={32}>
                  {isHotelAndFlightsToggleEnabled && <HotelFlightsToggleButton />}

                  {availableForPurchase && <>
                    <CSSBreakpoint max={isNumberOfNightsToggleEnabled ? 'tablet' : 'mobile'}>
                      <Group direction="vertical" gap={8}>
                        <OfferCapacityWithAgesSelectBox iconDisabled={isNumberOfNightsToggleEnabled} />
                        {isNumberOfNightsToggleEnabled && <Group direction="horizontal">
                          <OfferDurationSelectBox iconDisabled/>
                          <OfferCalendarDatePickerSelectBox iconDisabled roomTextDisabled/>
                        </Group>}
                        {!isNumberOfNightsToggleEnabled && <OfferCalendarDatePickerSelectBox />}
                        {showFlightInformation && <OfferAirportSelectBox iconDisabled={isNumberOfNightsToggleEnabled}/>}
                      </Group>
                    </CSSBreakpoint>

                    <CSSBreakpoint min={isNumberOfNightsToggleEnabled ? 'desktop' : 'tablet'}>
                      <DesktopFilterBlock fullWidth direction="horizontal" gap={8} horizontalAlign="center" className={cn({ open: !!activeFilter })}>
                        <StyledOfferCapacityWithAgesSelect
                              capacities={occupancyLimits}
                              disableMultiRoom={isFlightForceBundled(offer)}
                            />
                        {isNumberOfNightsToggleEnabled &&
                          <StyledOfferDurationSelect
                                dropdownPosition="bottom"
                                anchorRef={offerDurationSelectRef}
                                onClick={trackInteractionDurationEvent}
                                onSelect={trackInteractionDurationEvent}
                                onClose={trackInteractionDurationEvent}
                                footerOptions={{ onApply: () => stateDispatch({ type: OfferPageActions.toggleFilter, filter: 'checkin' }) }}
                              />
                            }
                        <StyledOfferCalendarDatePickerSelect />
                        {showFlightInformation && <StyledOfferAirportSelect />}
                        <Backdrop visible={!!activeFilter} data-testid="filter-backdrop" />
                      </DesktopFilterBlock>
                    </CSSBreakpoint>
                  </>}

                  {!isNumberOfNightsToggleEnabled && <VerticalSpacer gap={8}>
                    <Subtitle variant="subtitle3" align="center">
                      Select duration
                    </Subtitle>
                    <OfferDurationTabs />
                  </VerticalSpacer>}

                  {packages.length > 0 && !canBookMultiRoomTypes && (
                    <PackageOptions
                        showFlightInformation={showFlightInformation}
                        destinationAirport={destinationAirport}
                      />
                  )}
                  {canBookMultiRoomTypes && (
                    <MultipleRoomsBookingContent />
                  )}
                </VerticalSpacer>
              </LayoutContainer>
            </PackageOptionsPane>
            {availableForPurchase && isHotelOfferStickyFooterEnabled && <HotelOfferPageStickyFooter
                lowestPricePackage={offer.lowestPricePackage ?? offer.packages[0]}
                onScrollTo={onViewPackageOptions}
                offer={offer}
              />}
          </RelativeBlock>
        </OfferUpgradeModalContext>
      </OfferPageStateProvider>
    </ProductPaletteProvider>
  )
}

function mapStateToProps(state: App.State, ownProps: SetRequired<Partial<Props>, 'offer'>) {
  return {
    regionCode: state.geo.currentRegionCode,
    originAirports: state.geo.originAirports,
    airports: state.geo.airports,
    defaults: getInitialLeOfferPageState(state, ownProps.offer),
    routerLocation: state.router.location,
    currency: state.geo.currentCurrency,
    isMultiRoomEnabled: multiRoomEnabled(state, ownProps.offer),
    lereInlineYmalInView: !!state.recommendations.offerSharedState[ownProps.offer.id]?.ymalInlineInView,
    lereOfferExpired: hasOfferExpired(ownProps.offer),
    headlessMode: state.config.headlessMode,
    pathname: state.router.location.pathname,
    customerPortalURL: state.config.customerPortalURL,
    memberId: state.auth.account.memberId,
  }
}

export default connect(mapStateToProps)(FlashHotelContent)
